// Narration store — observa transicoes do matchSnapshot e dispara
// toasts narrativos. Equivalente ao detectEvents + detectD2Bubbles
// do vanilla, mas centralizado.
//
// Vocabulario literal e universal BR (spec aprovada pelo Leonardo):
//   "VOCE GANHOU A VAZA" / "VOCES GANHARAM A VAZA"
//   "VOCE VENCEU A MAO (+N)" / "VOCES VENCERAM A MAO (+N)"
//   "VOCE VENCEU A PARTIDA" / "VOCES VENCERAM A PARTIDA"
//   "VAZA EMPATADA" / "MAO MORTA"

import { writable, get } from 'svelte/store';
import { matchSnapshot } from './match';
import { userId } from './auth';
import { cardStrength } from '../lib/cards';
import { findMe2v2 } from '../lib/teams';
import { speak, clearPlayer, clearSpeech } from './speech';
import type { MatchSnapshot, Card, PlayerSnapshot } from './match';

export type ToastMessage = {
  text: string;
  kind: 'win' | 'lose' | 'draw';
  size: 'big' | 'small';
  key: number; // muda a cada push pra forcar re-animacao
};

export const toastMessage = writable<ToastMessage | null>(null);

// teamMatesOf retorna os IDs dos DOIS jogadores do time que contem `pid`
// (em 2v2). Leonardo 2026-05-31: quando o time aceita/corre do truco, o
// balao deve aparecer na cabeca dos DOIS parceiros, nao so de um — fica
// natural (o time reage junto) e disfarca o bot. Retorna [pid] em 1v1.
function teamMatesOf(snap: MatchSnapshot, pid: string): string[] {
  for (const team of [snap.team_a, snap.team_b]) {
    if (team && team.some((p) => p.id === pid)) {
      return team.map((p) => p.id);
    }
  }
  return [pid];
}

// speakTeam fala a mesma frase na cabeca dos dois parceiros do time de pid.
function speakTeam(snap: MatchSnapshot, pid: string, text: string, tone: 'shout' | 'ack' | 'run') {
  for (const id of teamMatesOf(snap, pid)) {
    if (id) speak(id, text, tone);
  }
}

let toastTimer: number | null = null;
let counter = 0;
function pushToast(text: string, kind: ToastMessage['kind'], size: ToastMessage['size']) {
  counter++;
  toastMessage.set({ text, kind, size, key: counter });
  if (toastTimer !== null) clearTimeout(toastTimer);
  const dur = size === 'big' ? 2000 : 1000;
  toastTimer = window.setTimeout(() => toastMessage.set(null), dur);
}

// Snapshot anterior pra detectar transicoes.
let prevSnap: MatchSnapshot | null = null;

// Estado per-hand pra evitar duplicar toasts:
// - lastTricksResolved: quantas vazas decididas ja narramos
// - finishedShown: id do match cujo finished toast ja foi mostrado
// - lastNarratedDeadRound: round_number da ultima mao narrada como morta.
//   Sem esse dedup, em reconexao pos-mao-morta o `prev` chega zerado e
//   o toast falso dispara; ou em mao-morta-seguinte-de-mao-morta a flag
//   `prev.round_dead` ja era true e o toast da 2a morta era SUPRIMIDO.
let lastTricksResolved = 0;
let finishedShownFor = '';
let lastNarratedDeadRound = -1;

// Marca timestamp da ultima vez que EU (este client) cliquei fugir
// ou correr. Quando o narration detecta "time correu" e essa marca e
// recente (<2s), foi eu — speak local ja foi feito. Senao foi parceiro,
// dispara speak na cabeca dele.
let myDecisionAt = 0;
export function markMyRunDecision() {
  myDecisionAt = Date.now();
}

// Helper: extrai plays da vaza atual (mesma logica do CurrentTrickPlays
// do bot — usa snap.p1/p2 ou team_a/b).
function getPlayersList(snap: MatchSnapshot): PlayerSnapshot[] {
  if (snap.format === '2v2') {
    return [...(snap.team_a || []), ...(snap.team_b || [])];
  }
  return [snap.p1, snap.p2].filter(Boolean) as PlayerSnapshot[];
}

// Quantas plays existem no snap em CADA player. Usado pra detectar
// quando uma vaza fechou (todas as plays do mesmo idx existem).
function countTrickPlays(snap: MatchSnapshot, trickIdx: 0 | 1 | 2): number {
  const slot = trickIdx === 0 ? 'first_play' : trickIdx === 1 ? 'second_play' : 'third_play';
  return getPlayersList(snap).filter((p) => p && p[slot] && p[slot]!.value).length;
}

// Quantas vazas estao COMPLETAS (todas plays preenchidas).
function tricksResolvedCount(snap: MatchSnapshot): number {
  const total = snap.format === '2v2' ? 4 : 2;
  let count = 0;
  for (const idx of [0, 1, 2] as const) {
    if (countTrickPlays(snap, idx) >= total) count++;
  }
  return count;
}

// Determina se "meu time" ganhou a vaza idx.
// 'win' | 'lose' | 'tie'
function trickOutcome(snap: MatchSnapshot, uid: string, idx: 0 | 1 | 2): 'win' | 'lose' | 'tie' {
  const slot = idx === 0 ? 'first_play' : idx === 1 ? 'second_play' : 'third_play';
  const myTeam: string[] = snap.format === '2v2' ? findMe2v2(snap, uid).mineIds : [uid];

  let bestStr = -1;
  let bestTeamMine: boolean | null = null;
  let tied = false;

  for (const p of getPlayersList(snap)) {
    const card = p[slot] as Card | undefined;
    if (!card?.value) continue;
    const s = cardStrength(card, snap.manilha ?? undefined);
    const isMine = myTeam.includes(p.id);
    if (s > bestStr) {
      bestStr = s;
      bestTeamMine = isMine;
      tied = false;
    } else if (s === bestStr && bestTeamMine !== null && bestTeamMine !== isMine) {
      tied = true;
    }
  }
  if (tied) return 'tie';
  if (bestTeamMine === true) return 'win';
  return 'lose';
}

// Score do meu time vs oposto, retornando (mine, opp).
function myAndOppScores(snap: MatchSnapshot, uid: string): [number, number] {
  if (snap.format === '2v2') {
    const seats = findMe2v2(snap, uid);
    const myScore = seats.meTeam === 'A' ? (snap.team_a_score ?? 0) : (snap.team_b_score ?? 0);
    const oppScore = seats.meTeam === 'A' ? (snap.team_b_score ?? 0) : (snap.team_a_score ?? 0);
    return [myScore, oppScore];
  }
  const meIsP1 = snap.p1?.id === uid;
  const my = meIsP1 ? (snap.p1.game_score ?? 0) : (snap.p2.game_score ?? 0);
  const opp = meIsP1 ? (snap.p2.game_score ?? 0) : (snap.p1.game_score ?? 0);
  return [my, opp];
}

// Detect: vaza recem-fechada (count subiu).
function detectNewTrickClosed(prev: MatchSnapshot | null, snap: MatchSnapshot) {
  const newResolved = tricksResolvedCount(snap);
  if (newResolved > lastTricksResolved && newResolved > 0) {
    const closedIdx = (newResolved - 1) as 0 | 1 | 2;
    const outcome = trickOutcome(snap, get(userId), closedIdx);
    const plural = snap.format === '2v2';
    if (outcome === 'tie') {
      pushToast('VAZA EMPATADA', 'draw', 'small');
    } else if (outcome === 'win') {
      pushToast(plural ? 'VOCÊS GANHARAM A VAZA' : 'VOCÊ GANHOU A VAZA', 'win', 'small');
    } else {
      pushToast(plural ? 'VOCÊS PERDERAM A VAZA' : 'VOCÊ PERDEU A VAZA', 'lose', 'small');
    }
    lastTricksResolved = newResolved;
  }
  // Reset quando uma nova mao comeca (round_points volta a 1, vira muda).
  if (prev && prev.manilha?.value !== snap.manilha?.value) {
    lastTricksResolved = 0;
  }
}

// Detect: mao decidida (score pulou).
function detectHandResolved(prev: MatchSnapshot | null, snap: MatchSnapshot) {
  if (!prev) return;
  const uid = get(userId);
  const [my, opp] = myAndOppScores(snap, uid);
  const [myPrev, oppPrev] = myAndOppScores(prev, uid);
  const plural = snap.format === '2v2';

  // Dedup por round_number: o backend incrementa em dealNextRound, entao
  // cada mao tem id unico. Toast so dispara quando ainda nao narrei pra
  // ESTA mao. Sobrevive a reconexao porque round_number sobrevive — antes
  // o check era `!prev.round_dead`, fragil quando `prev` chegava zerado.
  const currentRoundNum = snap.round_number ?? 0;
  if (snap.round_dead && currentRoundNum !== lastNarratedDeadRound) {
    pushToast('MÃO MORTA — EMPATE TRIPLO', 'lose', 'big');
    lastNarratedDeadRound = currentRoundNum;
    return;
  }
  if (my > myPrev) {
    const delta = my - myPrev;
    pushToast(
      plural ? `VOCÊS VENCERAM A MÃO (+${delta})` : `VOCÊ VENCEU A MÃO (+${delta})`,
      'win',
      'big',
    );
  } else if (opp > oppPrev) {
    pushToast(plural ? 'VOCÊS PERDERAM A MÃO' : 'VOCÊ PERDEU A MÃO', 'lose', 'big');
  }
}

// Detect: partida terminou (status -> finished).
function detectMatchFinished(prev: MatchSnapshot | null, snap: MatchSnapshot) {
  if (snap.status !== 'finished') return;
  if (finishedShownFor === snap.id) return;
  finishedShownFor = snap.id;

  const uid = get(userId);
  const mineIds: string[] = snap.format === '2v2' ? findMe2v2(snap, uid).mineIds : [uid];
  const wonByMyTeam = !!snap.winner && mineIds.includes(snap.winner);
  const draw = !snap.winner;
  const plural = snap.format === '2v2';

  if (draw) {
    pushToast('EMPATE', 'draw', 'big');
  } else if (wonByMyTeam) {
    pushToast(plural ? 'VOCÊS VENCERAM A PARTIDA' : 'VOCÊ VENCEU A PARTIDA', 'win', 'big');
  } else {
    pushToast(plural ? 'VOCÊS PERDERAM A PARTIDA' : 'VOCÊ PERDEU A PARTIDA', 'lose', 'big');
  }
}

// Detect: truco call/raise/refuse — comunica que o time pediu/aceitou/correu.
function detectTrucoTransitions(prev: MatchSnapshot | null, snap: MatchSnapshot) {
  if (!prev) return;
  const uid = get(userId);
  const mineIds: string[] = snap.format === '2v2' ? findMe2v2(snap, uid).mineIds : [uid];

  // Novo challenger (call ou raise muda truco_challenger_id).
  if (
    snap.truco_challenge_running &&
    snap.truco_challenger_id &&
    snap.truco_challenger_id !== prev.truco_challenger_id
  ) {
    const word = wordForLevel(snap.round_points);
    // 3 casos, nao 2: EU pedi / meu PARCEIRO pediu / OPONENTE pediu.
    // Antes comparava so com mineIds (o time inteiro) — parceiro
    // pedindo truco virava "VOCÊ PEDIU" (Leonardo 2026-06-11).
    const challenger = snap.truco_challenger_id;
    if (challenger === uid) {
      pushToast(`VOCÊ PEDIU ${word}`, 'win', 'small');
    } else if (mineIds.includes(challenger)) {
      pushToast(`PARCEIRO PEDIU ${word}`, 'win', 'small');
    } else {
      pushToast(`OPONENTE PEDIU ${word}`, 'lose', 'small');
    }
    // Em raise: limpa o balao do desafiante anterior (que ja foi
    // respondido com um "vou subir"). Aparece um novo balao no
    // novo desafiante.
    if (prev.truco_challenger_id) clearPlayer(prev.truco_challenger_id);
    // Balao STICKY do desafiante: "TRUCO!", "SEIS!" etc — fica na
    // cabeca dele ate alguem aceitar/correr/raise (request do
    // Leonardo: "ate a pessoa aceitar ou correr").
    speak(snap.truco_challenger_id, `${word}!`, 'shout', { sticky: true });
  }

  // Resolveu (estava pending, agora nao esta).
  if (prev.truco_challenge_running && !snap.truco_challenge_running) {
    // Limpa o balao sticky do desafiante.
    if (prev.truco_challenger_id) clearPlayer(prev.truco_challenger_id);
    const prevChallengerMine = !!prev.truco_challenger_id && mineIds.includes(prev.truco_challenger_id);
    if (snap.round_points > prev.round_points) {
      // ACEITO — valor subiu. Se quem aceitou foi meu PARCEIRO,
      // atribui ("PARCEIRO ACEITOU" — Leonardo 2026-06-11); senao
      // mantem o generico.
      const acceptorId = snap.truco_last_acceptor;
      if (acceptorId && acceptorId !== uid && mineIds.includes(acceptorId)) {
        pushToast(`PARCEIRO ACEITOU — VALE ${snap.round_points}`, prevChallengerMine ? 'win' : 'lose', 'small');
      } else {
        pushToast(`ACEITO — VALE ${snap.round_points}`, prevChallengerMine ? 'win' : 'lose', 'small');
      }
      // Backend expoe quem aceitou (truco_last_acceptor) — bubble nele
      // (bot OU humano, mesmo comportamento). Se foi EU local, ja
      // chamei speak no TrucoActions.
      // ACEITO: balao SO em quem aceitou (1). Felipe 2026-06-01: "os 2
      // so quando foge". Se foi EU local, ja chamei speak no TrucoActions.
      const acceptor = snap.truco_last_acceptor;
      if (acceptor && acceptor !== uid) {
        speak(acceptor, 'ACEITO!', 'ack');
      } else if (!acceptor && snap.format !== '2v2' && prev.truco_challenger_id) {
        const respondent = prev.p1?.id === prev.truco_challenger_id ? prev.p2?.id : prev.p1?.id;
        if (respondent) speak(respondent, 'ACEITO!', 'ack');
      }
    } else {
      // CORREU — score subiu pra um lado.
      const [my, opp] = myAndOppScores(snap, uid);
      const [myPrev, oppPrev] = myAndOppScores(prev, uid);
      if (my > myPrev) {
        pushToast('OPONENTE CORREU', 'win', 'small');
        // Backend agora expoe quem correu (truco_last_refuser) — bubble
        // SO no decider especifico (bot OU humano). Felipe 2026-05-21.
        const refuser = snap.truco_last_refuser;
        if (refuser) {
          // 2v2: balao "TÔ FORA!" nos DOIS do time que correu.
          if (snap.format === '2v2') {
            speakTeam(snap, refuser, 'TÔ FORA!', 'run');
          } else {
            speak(refuser, 'TÔ FORA!', 'run');
          }
        } else if (snap.format !== '2v2' && prev.truco_challenger_id) {
          // Fallback 1v1 antigo se backend nao popular o campo.
          const respondent = prev.p1?.id === prev.truco_challenger_id ? prev.p2?.id : prev.p1?.id;
          if (respondent) speak(respondent, 'TÔ FORA!', 'run');
        }
      } else if (opp > oppPrev) {
        pushToast(snap.format === '2v2' ? 'VOCÊS CORRERAM' : 'VOCÊ CORREU', 'lose', 'small');
        if (snap.format !== '2v2' && prev.truco_challenger_id) {
          const respondent = prev.p1?.id === prev.truco_challenger_id ? prev.p2?.id : prev.p1?.id;
          if (respondent) speak(respondent, 'CORRI!', 'run');
        } else if (snap.format === '2v2') {
          // MEU time correu — balao "TÔ FORA!" nos DOIS (eu + parceiro).
          const refuser = snap.truco_last_refuser;
          if (refuser) {
            speakTeam(snap, refuser, 'TÔ FORA!', 'run');
          } else {
            // Fallback antigo: fala no time inteiro via meus assentos.
            const seats = findMe2v2(snap, uid);
            for (const id of seats.mineIds) speak(id, 'TÔ FORA!', 'run');
          }
        }
      }
    }
  }
}

// Detect: mao de 11 resolvida (pending true → false). Mostra
// "PARCEIRO CORREU"/"PARCEIRO ACEITOU" se foi o parceiro (em 2v2)
// ou "OPONENTE CORREU"/"OPONENTE ACEITOU" se foi o outro time.
function detectManoEspecialResolved(prev: MatchSnapshot | null, snap: MatchSnapshot) {
  if (!prev) return;
  if (!prev.mano_especial_pending || snap.mano_especial_pending) return;

  const uid = get(userId);
  const [my, opp] = myAndOppScores(snap, uid);
  const [myPrev, oppPrev] = myAndOppScores(prev, uid);
  const oppGotPoint = opp > oppPrev;
  const myTeamGotPoint = my > myPrev;

  // Quem decidiu? Time em 11 era o que tem o mano_especial_player_id.
  const decider = prev.mano_especial_player_id;
  const mineIds: string[] = snap.format === '2v2' ? findMe2v2(snap, uid).mineIds : [uid];
  const meuTimeDecidiu = decider ? mineIds.includes(decider) : false;

  // Backend agora expoe quem decidiu — bubble specific (mesmo pra bot).
  // Felipe 2026-05-21: comportamento bot = humano (bubble so em quem
  // realmente clicou/decidiu, nao genericamente em ambos).
  const deciderPid = snap.mano_especial_last_decider;
  if (oppGotPoint) {
    if (meuTimeDecidiu) {
      pushToast(snap.format === '2v2' ? 'VOCÊS CORRERAM' : 'VOCÊ CORREU', 'lose', 'big');
      // Bubble no decider especifico. Se fui EU (myDecisionAt recente),
      // o speak local ja foi feito (TrucoActions/ManoEspecialBanner) —
      // nao duplica.
      if (snap.format === '2v2' && deciderPid && deciderPid !== uid) {
        speak(deciderPid, 'FUGI, PARCEIRO!', 'run');
      }
    } else {
      pushToast('OPONENTE CORREU', 'win', 'big');
      if (deciderPid) speak(deciderPid, 'TÔ FORA!', 'run');
    }
  } else if (!myTeamGotPoint) {
    // Score nao mudou → aceitou
    if (meuTimeDecidiu) {
      pushToast(snap.format === '2v2' ? 'VOCÊS ACEITARAM' : 'VOCÊ ACEITOU', 'win', 'small');
      // Se foi parceiro (decider != eu), bubble nele
      if (deciderPid && deciderPid !== uid) {
        speak(deciderPid, 'BORA!', 'ack');
      }
    } else {
      pushToast('OPONENTE ACEITOU', 'lose', 'small');
      // Bubble no oponente especifico que aceitou
      if (deciderPid) speak(deciderPid, 'BORA!', 'ack');
    }
  }
}

function wordForLevel(currentValue: number): string {
  // Word do PROXIMO nivel a pedir
  switch (currentValue) {
    case 1: return 'TRUCO';
    case 3: return 'SEIS';
    case 6: return 'NOVE';
    case 9: return 'DOZE';
    default: return 'TRUCO';
  }
}

// Subscribe — roda a cada mudanca de snapshot.
matchSnapshot.subscribe((snap) => {
  if (!snap) {
    prevSnap = null;
    lastTricksResolved = 0;
    lastNarratedDeadRound = -1;
    clearSpeech();
    return;
  }
  // Match novo (id mudou): reseta state e limpa balaoes stale.
  if (!prevSnap || prevSnap.id !== snap.id) {
    prevSnap = snap;
    lastTricksResolved = tricksResolvedCount(snap);
    lastNarratedDeadRound = -1;
    clearSpeech();
    return;
  }

  detectTrucoTransitions(prevSnap, snap);
  detectManoEspecialResolved(prevSnap, snap);
  detectNewTrickClosed(prevSnap, snap);
  detectHandResolved(prevSnap, snap);
  detectMatchFinished(prevSnap, snap);

  prevSnap = snap;
});
